import React, { useState } from 'react';
import Tab from '@lwt-helix/tab';
import { Button } from '@lwt-helix/buttons';
import { useCommonStore, useSearchStore } from '../../store/store';
import { terms, tabs, editCriteriaTerms } from '../constants/suggestedAgents';
import AddAgentListPopover from './common/AddAgentListPopover';
import Loader from '@lwt-helix/loader';
import { SuggestedAgentsResults } from './SuggestedAgentsResults';
import { useSuggestedAgents } from '../hooks/suggestedAgents';
import { useSuggestedAgentsTable } from '../hooks/suggestedAgentsTable';
import { views } from '../constants/savedAgents';
import {
    getFooterTimePeriodValue,
    getFooterIncDecProducerValue,
    getCurrentTabTimePeriod,
    validateGrowthFields,
    resolveTabDescription,
    getMlsToUpdate
} from './suggestedAgents/helpers/helpers';
import { buttonTerms, TimePeriods, toastMessages } from '../../constants';
import { ToastContainer } from '@lwt-helix/toast';
import Footer from './common/Footer';
import Modal from '@lwt-helix/modal';
import StickyFooter from '@lwt-helix/sticky-footer';
import { TodaysPicks } from './suggestedAgents/todaysPicks/TodaysPicks';
import EditCriteriaModal from './suggestedAgents/EditCriteriaModal';
import { useFeatureToggles } from '../../common/hooks/featureToggles';
import { setStateData } from '../../common/helpers/state';
import { Producers } from './suggestedAgents/Producers';
import { showToast, toastTypes } from '../../common/helpers/toast';
import { getCurrentSearchDates } from '../../common/helpers/utilities';

export const SuggestedAgents = () => {
    const [showEditCriteriaModal, setShowEditCriteriaModal] = useState(false);
    const [localMlsByTab, setLocalMlsByTab] = useState({});
    const {
        currentTabId,
        currentTabIndex,
        currentInactivityPeriod,
        currentTabTimePeriod,
        growthAnalysisPeriod,
        maxGrowth,
        minGrowth,
        selectedListingViewId,
        selectedModule,
        clearSelectedAgentIds,
        setCurrentTabIndex,
        setSearchFilterState,
        setSuggestedAgentsData,
        setTabDescription,
        setSuggestedAgentsMlsByTab,
        selectedMls,
        selectedLowPos,
        selectedNewProdPeriod,
        selectedInactivityPeriod,
        selectedTimePeriod,
        isLoading,
    } = useSearchStore(state => {
        const moduleState = state[state.selectedModule];
        const currentTabId = moduleState?.currentTabId;
        const currentTabState = moduleState?.searchOptions?.[currentTabId];
        const producers = currentTabState?.producers
        const currentInactivityPeriod = producers?.inactivity?.label || null;
        const isIncDecProducers =
        currentTabId === tabs.incProducers.id || currentTabId === tabs.decProducers.id;
        const selectedListingViewId = currentTabState?.display.selectedView.value;
        const isGrowthAnalysisView = selectedListingViewId === views.growthAnalysis;
        const currentTabTimePeriod = getCurrentTabTimePeriod(producers, isGrowthAnalysisView);
        const growthAnalysisPeriod = isIncDecProducers
            ? currentTabState?.producers?.growthTimeFrame?.value
            : TimePeriods[currentTabState?.growthAnalysisPeriod?.value];
        return ({
            currentTabId,
            currentTabIndex: moduleState?.currentTabIndex,
            currentInactivityPeriod,
            currentTabTimePeriod,
            growthAnalysisPeriod,
            maxGrowth: currentTabState?.producers?.maxGrowth,
            minGrowth: currentTabState?.producers?.minGrowth,
            searchOptions: moduleState?.searchOptions,
            selectedModule: state.selectedModule,
            clearSelectedAgentIds: state.clearSelectedAgentIds,
            setCurrentTabIndex: state.setCurrentTabIndex,
            setSearchFilterState: state.setSearchFilterState,
            setSuggestedAgentsData: state.setSuggestedAgentsData,
            setTabDescription: state.setTabDescription,
            setSuggestedAgentsMlsByTab: state.setSuggestedAgentsMlsByTab,
            selectedListingViewId,
            selectedLowPos: producers?.lowPos || producers?.lowPos,
            selectedMls: currentTabState?.producers?.mls ||  moduleState.selectedMls,
            selectedNewProdPeriod: currentTabState?.producers?.newProd,
            selectedInactivityPeriod: currentTabState?.producers?.inactivity,
            selectedTimePeriod:
                currentTabState?.producers?.timePeriod ||
                currentTabState?.producers?.newProd ||
                currentTabState?.producers?.timeFrame,
            isLoading: state.isLoading,
        });
    });
    const {
        timeIntervals,
        mlsProviders,
    } = useCommonStore((state) => ({
        timeIntervals: state.timeIntervals,
        mlsProviders: state.mlsProviders,
    }));
    
    // Switch to hide todays picks temporarily
    const { todaysPicks } = useFeatureToggles();
    const HIDE_TODAYS_PICKS = !todaysPicks.isEnabled;
    const tabValues = Object.values(tabs).filter(tab =>
        HIDE_TODAYS_PICKS ? tab.id !== tabs.todaysPicks.id : true
    );
    const isHiddenTab = HIDE_TODAYS_PICKS && currentTabId === tabs.todaysPicks.id;
    const safeTabId = isHiddenTab ? tabs.topProducers.id : currentTabId;
    const adjustedTabIndex = HIDE_TODAYS_PICKS ? currentTabIndex - 1 : currentTabIndex;

    const isCoopAgents = safeTabId === tabs.coopAgents.id;
    const isTodaysPicks = safeTabId === tabs.todaysPicks.id;
    const { gridRef, state, setState } = useSuggestedAgents();
    const tableSettings = useSuggestedAgentsTable({state, setState, growthAnalysisPeriod, currentTabId, selectedListingViewId});
    if (isCoopAgents && tableSettings.tableView.id === views.contactInformation) tableSettings.tableView.defaultSort = {};

    const timePeriodFooterTitle = getFooterTimePeriodValue(state.currentSearchDates, safeTabId);
    const selectedTimeFrameTitle = Object.keys(TimePeriods).find(key => TimePeriods[key] === selectedNewProdPeriod?.value);
    const productionTitle = getFooterIncDecProducerValue({
        currentTabId,
        inactivityPeriod: selectedInactivityPeriod?.label,
        minGrowth,
        maxGrowth,
        timeFrame: selectedTimeFrameTitle
    });

    const columnProps = [
        {
            children: (
                <Footer
                    mlsName={localMlsByTab?.[currentTabId]?.shortDescription || selectedMls?.shortDescription}
                    propertyTypeNames={state.propertyTypeNames}
                    timePeriodTitle={timePeriodFooterTitle}
                    productionTitle={productionTitle}
                />
            ),
            sm: 12,
            align: 'left'
        }
    ];

    const renderTabTitle = (tab) => {
        if (tab.id === tabs.todaysPicks.id && tab.badgeCount) {
            return (
                <span className="d-flex align-items-center">
                    {tab.label}
                    <span className="todays-picks-badge">{tab.badgeCount}</span>
                </span>
            );
        }
        return tab.label;
    };

    const onClose = () => {
        setStateData('showModal', false, setState);
        setStateData('selectedFilterValues', {}, setState);
    };

    const handleSave = () => {
        const selectedTabState = state.selectedFilterValues;
        const isGrowthField = validateGrowthFields(selectedTabState, currentTabId);
        if (isGrowthField) {
            showToast(
                toastTypes.error,
                toastMessages.error.emptyProducers(currentTabId === tabs.incProducers.id),
                setState
            );
            return;
        }
        setStateData('toastProps', {}, setState);
        clearSelectedAgentIds();
        const selectedMlsId = selectedTabState?.[currentTabId]?.mls?.value || selectedMls?.mlsId;
        setLocalMlsByTab(state => ({ ...state, [currentTabId]: mlsProviders.find(mls => mls.mlsId === selectedMlsId)}));
        setSearchFilterState(
            selectedTabState?.[currentTabId],
            selectedTabState.filterId,
            selectedTabState.stateId,
            tabs[currentTabId].hasRefreshSearchBtn
        );
        setStateData(
            'currentSearchDates',
            getCurrentSearchDates(timeIntervals, selectedTimePeriod?.value),
            setState
        );
        setSuggestedAgentsData({ mlsId: selectedMlsId, setState, currentTabId });
        setTabDescription(
            resolveTabDescription({
                selectedTabState,
                currentTabId,
                currentTabTimePeriod,
                currentInactivityPeriod,
                selectedLowPos
            })
        );
        const mlsToUpdate = getMlsToUpdate({ selectedTabState, currentTabId, mlsProviders });
        if (mlsToUpdate) {
            setSuggestedAgentsMlsByTab(mlsToUpdate, selectedModule, currentTabId);
        }
        onClose();
    };

    const tabItems = tabValues.map((tab, i) => {
        const isTodaysPicksTab = tab.id === tabs.todaysPicks.id;
        return {
            title: renderTabTitle(tab),
            content: {
                jsx: adjustedTabIndex === i && (
                    <>
                        {isTodaysPicksTab ? (
                            <TodaysPicks />
                        ) : (
                            <>
                                <SuggestedAgentsResults
                                    tabId={tab.id}
                                    tableSettings={tableSettings}
                                    gridRef={gridRef}
                                    rowData={state.rowDataByTab[safeTabId]?.results}
                                    setState={setState}
                                    state={state}
                                    tabIndex={adjustedTabIndex}
                                />
                                <ToastContainer
                                    position='toast-bottom-right'
                                    dataLwtId='validation-toast'
                                    toastProps={state.toastProps}
                                />
                                <StickyFooter columnProps={columnProps} />
                            </>
                        )}
                    </>
                )
            }
        };
    });
    const isSaveBtnDisabled = !state.selectedFilterValues?.[currentTabId] || !state.selectedFilterValues.filterId || !state.selectedFilterValues.stateId;
    const modalButtons = (
        <>
            <Button
                dataLwtId="cancel-edit-criteria"
                color="secondary"
                onClick={onClose}
            >
                {buttonTerms.cancel}
            </Button>
            <Button
                dataLwtId="save-edit-criteria"
                color="primary"
                onClick={handleSave}
                disabled={isSaveBtnDisabled}
            >
                {buttonTerms.save}
            </Button>
        </>
    );
    
    const shouldShowLoader = !isTodaysPicks && (
        isLoading ||
        !state.rowDataByTab[currentTabId]?.results ||
        state.rowDataByTab[currentTabId]?.results?.length === undefined
    );

    const handleEditCriteriaClick = () => {
        if (currentTabId !== tabs.todaysPicks.id) {
            const modalProps = {
                onClose,
                show: true,
                showFooter: true,
                size: 'sm',
                title: buttonTerms.editCriteria,
            };
            setState(state => ({ ...state, modalProps, showModal: true }));
            return;
        }
        setShowEditCriteriaModal(true);
    };

    const handleCloseEditCriteria = () => {
        setShowEditCriteriaModal(false);
        setStateData('modalProps', undefined, setState);
    };
    const revisedModalProps = state.modalProps?.title === buttonTerms.editCriteria
        ? {...state.modalProps,
            buttons: modalButtons,
            children: <Producers
                    togglePopover={() => setStateData('showModal', !state.showModal, setState)}
                    setState={setState}
                    suggestedAgentsState={state} />}
        : state.modalProps;

    return (
        <>
            <Modal {...revisedModalProps} show={state.showModal} />
            <EditCriteriaModal
                show={showEditCriteriaModal}
                onClose={handleCloseEditCriteria}
            />
            <div id='suggested-agents' className='py-2'>
                <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                    <h4 className='mr-1 font-gray-primary'>
                        {terms.suggestedAgents}
                    </h4>
                    {!isLoading && (
                        <Button
                            onClick={handleEditCriteriaClick}
                            className="helix-bg-brand persist-bg text-white"
                            color='primary'
                        >
                            {editCriteriaTerms.editCriteria}
                        </Button>
                    )}
                </div>

                <div className='flex-grow-0'>
                    {shouldShowLoader
                        ? <Loader />
                        : <Tab
                            tabTextTransform='none'
                            navSize={2}
                            contentSize={2}
                            className='secondary'
                            items={tabItems}
                            tabs={tabValues}
                            currentIndex={adjustedTabIndex}
                            setCurrentIndex={(i) => {
                                const storeIndex = HIDE_TODAYS_PICKS ? i + 1 : i;
                                setCurrentTabIndex(storeIndex, Object.values(tabs));
                            }}
                         />
                    }
                </div>
                <AddAgentListPopover />
            </div>
        </>
    );
};
